import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
// import classNames from 'classnames';

import { IAddress, ICity, AddressPrediction } from 'types';
import { getAddressLookupIsLoading, getAddressLookupResults, getAddressLookupError } from 'store/modules/address-lookup/selectors';
import { actionLookup, actionDetails } from 'store/modules/address-lookup/actions';
import { getErrorMessageFor } from 'tools/utilities/getErrorMessageFor';
import { isValidPostCode } from 'tools/utilities/validators/postCode';
import { getCities, getRegions } from 'store/modules/options/selectors';
import { TextInput } from '../TextInput/TextInput';
import { SelectInput } from '../SelectInput/SelectInput';
import { AutoComplete } from '../AutoComplete/AutoComplete';
import { Button } from '../Button/Button';
import { MaskedInput } from '../MaskedInput/MaskedInput';
import { FuzzyLookup } from '../FuzzyLookup/FuzzyLookup';

interface Props {
  id: string;
  onChange: (address: IAddress) => void;
  onValid: (isValid: boolean) => void;
  defaultValues?: IAddress;
  touchedNext: boolean;
  errorMessageIntlId: string;
  overrideAddressIsValid?: boolean;
}

export const AddressFinder = (props: Props) => {
  const {
    id,
    onChange,
    defaultValues,
    onValid,
    touchedNext,
    errorMessageIntlId,
    overrideAddressIsValid,
  } = props;

  const intl = useIntl();
  const dispatch = useDispatch();

  const isAddressLookupLoading = useSelector(getAddressLookupIsLoading);
  const addressResults = useSelector(getAddressLookupResults);
  const addressLookupError = useSelector(getAddressLookupError);
  const cities = useSelector(getCities);
  const regions = useSelector(getRegions);

  const [address, setAddress] = useState<AddressPrediction | undefined>(defaultValues?.address);
  const [manualEntry, setManualEntry] = useState(defaultValues?.isManual || false);
  const [addressLineOne, setAddressLineOne] = useState(defaultValues?.addressLineOne || '');
  const [addressLineTwo, setAddressLineTwo] = useState(defaultValues?.addressLineTwo || '');
  const [city, setCity] = useState<ICity | undefined>(defaultValues?.city);
  const [postCode, setPostCode] = useState(defaultValues?.postCode || '');
  const [region, setRegion] = useState<ICity | undefined>(defaultValues?.region);
  const [country, setCountry] = useState(defaultValues?.country || '');

  const addressIsValid = overrideAddressIsValid || (!manualEntry && !!address);
  const addressLineOneIsValid = !!addressLineOne;
  // const addressLineTwoIsValid = !!addressLineTwo; // Not checking addressLineTwo
  const cityIsValid = !!city;
  const postCodeIsValid = isValidPostCode(postCode);
  const regionIsValid = !!region;
  const countryIsValid = !!country;

  const isValid = (addressIsValid) || (manualEntry && addressLineOneIsValid && cityIsValid && postCodeIsValid && regionIsValid && countryIsValid);

  const clearManualInputs = useCallback(() => {
    setAddressLineOne('');
    setAddressLineTwo('');
    setCity(undefined);
    setPostCode('');
    setRegion(undefined);
    setCountry('');
  }, []);

  useEffect(() => {
    onValid(isValid);
  }, [onValid, isValid]);

  useEffect(() => {
    if (address) {
      setManualEntry(false);
      clearManualInputs();
    }
  }, [address, clearManualInputs, setManualEntry]);

  useEffect(() => {
    onChange({
      address,
      isManual: manualEntry,
      addressLineOne,
      addressLineTwo,
      city,
      postCode,
      region,
      country,
    });
  }, [onChange, address, manualEntry, addressLineOne, addressLineTwo, city, postCode, region, country]);

  return (
    <div className="AddressFinder">
      {!manualEntry && (
        <div className="AddressFinder-autocomplete">
          <AutoComplete
            id={`search-${id}`}
            items={addressResults.map(({ description }) => description)}
            label={intl.formatMessage({ id: 'ADDRESS_FINDER' })}
            onInputValueChange={(value) => {
              if (value && value.length >= 3) {
                dispatch(actionLookup(value));
              }
            }}
            onSelect={(value) => {
              const addressPredictionActual = addressResults.find(({ description }) => description === value);
              if (addressPredictionActual) {
                setAddress(addressPredictionActual);
                dispatch(actionDetails(addressPredictionActual.place_id));
              }
            }}
            placeholder={intl.formatMessage({ id: 'START_TYPING_ADDRESS' })}
            selectedItem={address?.description || ''}
            isLoading={isAddressLookupLoading}
            error={touchedNext && getErrorMessageFor(addressIsValid || manualEntry, errorMessageIntlId, intl)}
            noResultsMessage={(
              <div className="AddressFinder-noResults">
                <FormattedMessage tagName="p" id="NO_RESULTS" />
                {addressLookupError && <p>{addressLookupError}</p>}
              </div>
            )}
            clearLabel={intl.formatMessage({ id: 'BUTTON_CLEAR' })}
          />
        </div>
      )}

      <Button
        label={intl.formatMessage({ id: manualEntry ? 'HIDE_ADDRESS_MANUAL' : 'ENTER_ADDRESS_MANUAL' })}
        className="AddressFinder-manual Button Button-link"
        onClick={() => {
          if (!manualEntry) {
            setAddress(undefined);
          } else {
            clearManualInputs();
          }

          setManualEntry(!manualEntry);
        }}
      />

      {/* @TODO Could move this to own component */}
      {manualEntry && (
        <div className="AddressFinder-manual">
          <TextInput
            placeholder=""
            className=""
            id={`addressLineOne-${id}`}
            type="text"
            onChange={setAddressLineOne}
            value={addressLineOne}
            label={intl.formatMessage({ id: 'ADDRESS_LINE_ONE' })}
            error={touchedNext && getErrorMessageFor(addressLineOneIsValid, 'ERROR_ADDRESS_LINE_ONE', intl)}
          />
          <TextInput
            placeholder=""
            className=""
            id={`addressLineTwo-${id}`}
            type="text"
            onChange={setAddressLineTwo}
            value={addressLineTwo}
            label={intl.formatMessage({ id: 'ADDRESS_LINE_TWO' })}
          />
          <div className="AddressFinder-manual-sideBySide">
            <FuzzyLookup
              placeholder={intl.formatMessage({ id: 'SELECT' })}
              id={`city-${id}`}
              items={cities.map(({ name }) => name)}
              onChange={(val) => {
                const selectedCity = cities.find(({ name }) => val === name);
                setCity(selectedCity);
              }}
              value={city?.name || ''}
              label={intl.formatMessage({ id: 'CITY' })}
              errorMessage={touchedNext && getErrorMessageFor(cityIsValid, 'ERROR_CITY', intl)}
              noResultsMessage={<FormattedMessage tagName="p" id="CITY_NO_RESULTS" />}
              clearLabel={intl.formatMessage({ id: 'BUTTON_CLEAR' })}
            />
            <MaskedInput
              mask="9999"
              placeholder=""
              className="TextInput--3of4"
              id={`postCode-${id}`}
              type="tel"
              onChange={setPostCode}
              value={postCode}
              label={intl.formatMessage({ id: 'POST_CODE' })}
              error={touchedNext && getErrorMessageFor(postCodeIsValid, 'ERROR_POST_CODE', intl)}
            />
          </div>

          <div className="AddressFinder-manual-sideBySide">
            <SelectInput
              className="SelectInput--1of1"
              id={`region-${id}`}
              onChange={(val) => {
                const selectedRegion = regions.find(({ name }) => val === name);
                setRegion(selectedRegion);
              }}
              defaultValue={region?.name}
              placeholder={intl.formatMessage({ id: 'SELECT' })}
              label={intl.formatMessage({ id: 'REGION' })}
              error={touchedNext && getErrorMessageFor(regionIsValid, 'ERROR_REGION', intl)}
              options={regions.map(({ name }) => name)}
            />
            <TextInput
              type="text"
              className="SelectInput--3of4"
              id={`country-${id}`}
              onChange={setCountry}
              value={country}
              placeholder={intl.formatMessage({ id: 'COUNTRY' })}
              label={intl.formatMessage({ id: 'COUNTRY' })}
              error={touchedNext && getErrorMessageFor(countryIsValid, 'ERROR_COUNTRY', intl)}
            />
          </div>

        </div>
      )}
    </div>
  );
};
